// src/pages/sow-draft/ProposalUpload.jsx
//
// Optional proposal-document upload + parse, feeding extracted text into
// SOW generation as extra context. Split out of SowDraftPage.jsx; reuses
// the shared FileDropzone instead of the bespoke drag/drop markup it had.

import PropTypes from "prop-types";
import { Button, FileDropzone } from "../../shared/ui";
import { getFileIcon } from "./sowDraftHelpers";

export default function ProposalUpload({
  files,
  onFilesChange,
  parsed,
  uploading,
  error,
  onDismissError,
  onParse,
  acceptedTypes,
}) {
  return (
    <div className="flex flex-col gap-3">
      <FileDropzone
        multiple
        accept={acceptedTypes.split(",")}
        files={files}
        onChange={onFilesChange}
        maxTotalSize={50 * 1024 * 1024}
      />

      {error && (
        <div className="flex items-center justify-between gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          <span>{error}</span>
          <button type="button" onClick={onDismissError} className="text-red-400 hover:text-red-600">
            ✕
          </button>
        </div>
      )}

      {files.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button size="sm" onClick={onParse} disabled={uploading || files.length === 0} loading={uploading}>
            {parsed ? "Re-parse Files" : "Parse & Extract Content"}
          </Button>
          {parsed && (
            <span className="text-xs text-muted">
              {parsed.success_count} file(s) parsed — {parsed.total_chars?.toLocaleString()} chars extracted
            </span>
          )}
        </div>
      )}

      {parsed?.files?.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {parsed.files.map((f) => (
            <li key={f.filename} className="flex items-center gap-2 text-xs text-muted">
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                {getFileIcon(f.filename)}
              </span>
              <span className="truncate">{f.filename}</span>
              <span className="shrink-0">
                {f.status === "success" ? "— Parsed" : f.status === "partial" ? "— Partial" : "— Error"}
                {f.char_count > 0 ? ` (${f.char_count.toLocaleString()} chars)` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

ProposalUpload.propTypes = {
  files: PropTypes.array.isRequired,
  onFilesChange: PropTypes.func.isRequired,
  parsed: PropTypes.object,
  uploading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onDismissError: PropTypes.func.isRequired,
  onParse: PropTypes.func.isRequired,
  acceptedTypes: PropTypes.string.isRequired,
};
